import { Button } from "@mui/material"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import Iconify from "minimals-template/components/Iconify"
import { useParams } from "react-router"
import { isGroupValid } from "../group/lib/is-group-valid"
import { errorSnackbar } from "lib/snackbar/error-snackbar"
import { successSnackbar } from "lib/snackbar/success-snackbar"
import { warningSnackbar } from "lib/snackbar/warning-snackbar"
import { isLockedToLegacy } from "authoring/lib/enum/project-phase.mjs"
import { applyGroup } from "../group/controller/apply-group-edits"
import { useDialog } from "lib/@hooks/useDialog"
import { MissingGroupAmendsDialog } from "../group/components/missing-group-amends-dialog"
import { getMissingGroupAmends, hasAnyGroupAmendsContent } from "../group/lib/get-missing-group-amends"
import { GroupType } from "authoring/lib/enum/group-type.mjs"
import { OWNERSHIP_TRACKED_FIELD } from "authoring/lib/enum/unlockable-group-types.mjs"
import { SeedSource } from "authoring/lib/enum/seed-source.mjs"
import { getGroupSchedulesField } from "authoring/lib/enum/group-schedules-field.mjs"
import { getDraftOrPublishedSchedules } from "../controller/get-draft-or-published-schedules"
import { uniqueId } from "library/database/split-id"
import { ApplyGroupDialog } from "../group/components/apply-group-dialog"
import { OptOutNewScheduleAmendsDialog } from "../group/components/opt-out-new-schedule-amends-dialog"
import { updateProjectGroup } from "../group/controller/update-project-group"
import { refreshOnlyFor } from "lib/graphql/api"
import { IS_LOADING } from "lib/graphql/return-is-loading-on-each-request"
import { withInitialValue } from "lib/graphql/with-initial-value"

export function ApplyGroupButton({ ...props }) {
    const { projectId, groupId } = useParams()
    const { group, project } = useBoundContext()
    const sentToLegacy = isLockedToLegacy(project?.phase)
    const applyGroupDialog = useDialog(<ApplyGroupDialog />)
    const missingGroupAmendsDialog = useDialog(<MissingGroupAmendsDialog />)
    const optOutNewScheduleAmendsDialog = useDialog(<OptOutNewScheduleAmendsDialog />)

    // Fetched fresh (not the group's own stored snapshot) so the note-requirement check below can
    // tell a schedule that's genuinely already published to legacy apart from one that's still new,
    // even when the group's snapshot of it is stale.
    const schedulesField = getGroupSchedulesField(group?.type)
    const groupScheduleIds = (group?.[schedulesField] ?? []).map(getAddScheduleId)
    const freshSchedulesResult = getDraftOrPublishedSchedules.useResults(
        { projectId, scheduleIds: groupScheduleIds },
        refreshOnlyFor(projectId),
        withInitialValue(IS_LOADING)
    )
    const freshSchedulesLoading = freshSchedulesResult === IS_LOADING

    const isValid = isGroupValid(group)

    if (freshSchedulesLoading) return null

    const freshSchedulesById = (freshSchedulesResult ?? []).keyBy("id")

    return (
        <Button
            data-cy="apply-to-schedules"
            startIcon={<Iconify icon={"charm:tick"} />}
            {...props}
            onClick={handleClick}
            disabled={group?.readOnly || !isValid || sentToLegacy}
        >
            Apply to Schedules
        </Button>
    )

    async function handleClick() {
        try {
            const missingTaskLabels = getMissingGroupAmends(group, { freshSchedulesById })
            const hasMissingAmends = missingTaskLabels.length > 0
            if (hasMissingAmends) {
                await missingGroupAmendsDialog({
                    missingTaskLabels,
                    groupType: group?.type,
                })
                return
            }

            const newSchedules = getNewGroupSchedules(group)
            let selections = null
            if (newSchedules.length > 0 && hasAnyGroupAmendsContent(group)) {
                selections = await optOutNewScheduleAmendsDialog({ newSchedules })
                if (!selections) return
            }

            const scheduleCount = getGroupScheduleCount(group)
            const isReapply = Boolean(group?.[OWNERSHIP_TRACKED_FIELD[group?.type]])
            const confirmed = await applyGroupDialog({
                groupName: group?.name,
                groupType: group?.type,
                scheduleCount,
                isReapply,
            })
            if (!confirmed) return

            if (selections) {
                const updated = await persistRecordAmendsSelections({ projectId, groupId, group, selections })
                if (!updated) {
                    errorSnackbar("Failed to update note settings for new schedules")
                    return
                }
            }

            const result = await applyGroup({ projectId, groupId })
            if (!result?.success) {
                errorSnackbar("Failed to apply to schedules")
                return
            }

            successSnackbar("Successfully applied to schedules")
            if (result?.skippedAlreadyPublishedSchedules) {
                warningSnackbar("Already-published schedules were skipped")
            }
        } catch (error) {
            console.error(error)
            errorSnackbar(error?.message ?? "Unexpected error when applying to schedules")
        }
    }

    function getGroupScheduleCount(group = {}) {
        if (group?.type === GroupType.OVERWRITE) {
            const similarScheduleIds = group?.similarTasks?.map(getSimilarScheduleId) ?? []
            const overwriteSchedules = [group?.scheduleId, ...similarScheduleIds].map(uniqueId)

            return countUniqueSchedules(overwriteSchedules)
        }

        if (group?.type === GroupType.INTRODUCTION) {
            const introSchedules = (group?.introTypeSchedules?.map(getAddScheduleId) ?? []).map(uniqueId)
            return countUniqueSchedules(introSchedules)
        }
        if (group?.type === GroupType.LEADING_SUMMARY) {
            const leadingSummarySchedules = (group?.leadingSummaryTypeSchedules?.map(getAddScheduleId) ?? []).map(
                uniqueId
            )
            return countUniqueSchedules(leadingSummarySchedules)
        }

        const addSchedules = (group?.addTypeSchedules?.map(getAddScheduleId) ?? []).map(uniqueId)
        return countUniqueSchedules(addSchedules)
    }

    function countUniqueSchedules(scheduleIds = []) {
        const unique = new Set()
        scheduleIds.forEach((id) => {
            if (id) unique.add(id)
        })
        return unique.size
    }

    function getSimilarScheduleId(task = {}) {
        return task?.scheduleId
    }

    function getAddScheduleId(item = {}) {
        return item?.id
    }

    function getNewGroupSchedules(group = {}) {
        const schedulesField = getGroupSchedulesField(group?.type)
        if (!schedulesField) return []
        return (group[schedulesField] ?? []).filter(({ seedSource }) => seedSource === SeedSource.NEW)
    }

    async function persistRecordAmendsSelections({ projectId, groupId, group, selections }) {
        const schedulesField = getGroupSchedulesField(group?.type)
        const updatedList = (group[schedulesField] ?? []).map(({ id, code, title, seedSource, recordAmends }) => ({
            id,
            code,
            title,
            seedSource,
            recordAmends: seedSource === SeedSource.NEW ? !!selections[id] : recordAmends,
        }))
        return updateProjectGroup({ projectId, group: { id: groupId, [schedulesField]: updatedList } })
    }
}
